import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { useAuth } from "../../context/AuthContext";
import { fetchMyEnrollments } from "./clientCourseService";
import { LEVEL_FALLBACK_IMG } from "./CourseCard";
import "./courses.css";

const STATUS_CONFIG = {
  ACTIVE:          { label: "Active",               cls: "ACTIVE"          },
  PENDING_PAYMENT: { label: "Awaiting Confirmation", cls: "PENDING_PAYMENT" },
  CANCELLED:       { label: "Cancelled",             cls: "CANCELLED"       },
  INACTIVE:        { label: "Inactive",              cls: "CANCELLED"       },
};

const MyCoursesPage = () => {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?redirect=/my-courses", { replace: true }); return; }
    fetchMyEnrollments()
      .then((res) => setEnrollments(res.data || []))
      .catch(() => setEnrollments([]))
      .finally(() => setLoading(false));
  }, [authLoading, user, navigate]);

  if (authLoading || loading) {
    return (
      <>
        <Navbar />
        <div className="cc-loading"><div className="cc-spinner" /><p>Loading your courses…</p></div>
      </>
    );
  }

  const active   = enrollments.filter((e) => e.status === "ACTIVE");
  const pending  = enrollments.filter((e) => e.status === "PENDING_PAYMENT");
  const others   = enrollments.filter((e) => e.status !== "ACTIVE" && e.status !== "PENDING_PAYMENT");

  const EnrollmentCard = ({ e }) => {
    const cfg        = STATUS_CONFIG[e.status] || { label: e.status, cls: "CANCELLED" };
    const progress   = e.totalDays > 0 ? Math.round((e.currentDay / e.totalDays) * 100) : 0;
    const isPending  = e.status === "PENDING_PAYMENT";
    const isActive   = e.status === "ACTIVE";

    return (
      <div key={e.enrollmentId} className="cc-my-card" style={{ opacity: isPending || isActive ? 1 : 0.6 }}>
        <img
          src={e.thumbnailUrl || LEVEL_FALLBACK_IMG[e.level]}
          alt={e.courseName}
          className="cc-my-card-thumb"
        />

        <div className="cc-my-card-body">
          <div className="cc-my-card-top">
            <p className="cc-my-card-name">{e.courseName}</p>
            <span className={`cc-enroll-status cc-enroll-${cfg.cls}`}>{cfg.label}</span>
          </div>

          <div className="cc-my-card-meta" style={{ fontSize: "1.1rem" }}>
            <span>{e.mode === "BATCH" ? "👥 Group" : "👤 Personal"} · {e.batchName}</span>
            <span>📅 {e.batchStartDate ? new Date(e.batchStartDate + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "TBD"}</span>
            {isActive && e.attendedCount != null && (
              <span>✅ {e.attendedCount}/{e.totalDays} sessions done</span>
            )}
          </div>

          {isActive && e.totalDays > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.1rem", color: "#6b7280", marginBottom: 4 }}>
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <div style={{ height: 6, background: "#e5e7eb", borderRadius: 4 }}>
                <div style={{ width: `${progress}%`, height: "100%", background: "#007BFF", borderRadius: 4 }} />
              </div>
            </div>
          )}

          {isPending && (
            <p style={{ fontSize: "0.78rem", color: "#d97706", margin: "8px 0 0", fontWeight: 500 }}>
              ⏳ Admin will contact you on your registered WhatsApp/phone to confirm payment.
            </p>
          )}
        </div>

        <div className="cc-my-card-footer">
          <span style={{ fontSize: "1.1rem", color: "#9ca3af" }}>
            {e.enrolledAt
              ? `Enrolled ${new Date(e.enrolledAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`
              : isPending ? "Pending confirmation" : ""}
          </span>
          {isActive ? (
            <button
              className="cc-btn cc-btn-primary"
              style={{ padding: "8px 18px", fontSize: "0.9rem" }}
              onClick={() => navigate(`/my-courses/${e.enrollmentId}`)}
            >
              View →
            </button>
          ) : (
            <button
              className="cc-btn cc-btn-outline"
              style={{ padding: "8px 18px", fontSize: "0.9rem" }}
              onClick={() => navigate(`/courses/${e.courseSlug || e.courseId}`)}
            >
              View Course
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <div className="cc-page" style={{ paddingTop: 24 }}>
        <div className="cc-hero" style={{ marginBottom: 24 }}>
          <h1>My Courses</h1>
          <p>Track your enrolled courses and progress</p>
        </div>

        {enrollments.length === 0 ? (
          <div className="cc-empty">
            <div className="cc-empty-icon">📚</div>
            <p>You haven't enrolled in any courses yet.</p>
            <button className="cc-btn cc-btn-primary" onClick={() => navigate("/courses")}>
              Browse Courses
            </button>
          </div>
        ) : (
          <>
            {active.length > 0 && (
              <section style={{ marginBottom: 32 }}>
                <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#374151", marginBottom: 12 }}>Active</h3>
                <div className="cc-my-grid">
                  {active.map((e) => <EnrollmentCard key={e.enrollmentId} e={e} />)}
                </div>
              </section>
            )}

            {pending.length > 0 && (
              <section style={{ marginBottom: 32 }}>
                <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#374151", marginBottom: 12 }}>Awaiting Confirmation</h3>
                <div className="cc-my-grid">
                  {pending.map((e) => <EnrollmentCard key={e.enrollmentId} e={e} />)}
                </div>
              </section>
            )}

            {others.length > 0 && (
              <section>
                <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#374151", marginBottom: 12 }}>Others</h3>
                <div className="cc-my-grid">
                  {others.map((e) => <EnrollmentCard key={e.enrollmentId} e={e} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default MyCoursesPage;
